import {
  AXES,
  BUTTON_ACTIONS,
  PREVENT_DEFAULT_CODES,
  defaultBindings,
  type AxisName,
  type Bindings,
  type ButtonAction,
} from './actions';

/** Where an axis value came from (see `axisSource`). */
export type AxisSource = 'keyboard' | 'gamepad' | 'touch' | 'virtual' | null;

const GAMEPAD_DEADZONE = 0.18;
/** Minimum seconds between two wheel "presses" (a free-spinning wheel sends dozens of events). */
const WHEEL_REPEAT_MS = 140;

/**
 * Radial dead zone for a stick (x, y): inside `dz` of centre reads 0, the rest is rescaled so it still
 * reaches 1 at the rim. Applied per stick, not per axis: a per-axis zone snapped a slightly diagonal
 * push to a pure up / down / left / right (a walk that would not go a few degrees off the axis).
 */
export function radialDeadzone(x: number, y: number, dz: number): [number, number] {
  const m = Math.hypot(x, y);
  if (m <= dz || m === 0) return [0, 0];
  const k = Math.min(1, (m - dz) / (1 - dz)) / m;
  return [x * k, y * k];
}

/** Mouse events count only over the game canvas: clicks on menus, panels and the HUD never reach gameplay. */
function overCanvas(e: Event): boolean {
  const t = e.target as { tagName?: string } | null;
  return !!t && t.tagName === 'CANVAS';
}

/**
 * Unified keyboard + mouse + gamepad + touch + virtual (test) input. Mouse buttons are treated as keys
 * (`Mouse0`..`Mouse2`) and wheel notches as presses (`WheelUp` / `WheelDown`), see actions.ts. The touch
 * layer (src/ui/touchControls.ts) writes held buttons and axes through `setTouchButton` /
 * `setTouchAxes`; it never fakes key events.
 *
 * Two edge-detection clocks:
 *  - `pressed(a)`      true during exactly one FIXED step after the press (simulation logic).
 *  - `framePressed(a)` true during exactly one RENDER frame after the press (UI, pause — works while paused).
 *
 * The game calls `beginFrame()` once per rendered frame and `beginStep()` before every fixed step.
 */
export class InputManager {
  bindings: Bindings = defaultBindings();
  lastDevice: 'keyboard' | 'gamepad' | 'touch' = 'keyboard';
  /** When false all queries return neutral values (e.g. while a menu has focus). */
  enabled = true;

  private keysDown = new Set<string>();
  private padButtonsDown = new Set<number>();
  private padButtonValues: number[] = [];
  private padAxesValues: number[] = [];
  private pendingStep = new Set<ButtonAction>();
  private pendingFrame = new Set<ButtonAction>();
  private stepPressed = new Set<ButtonAction>();
  private framePressedSet = new Set<ButtonAction>();
  private virtualAxes = new Map<AxisName, number>();
  private virtualButtons = new Set<ButtonAction>();
  private touchAxes = new Map<AxisName, number>();
  private touchButtons = new Set<ButtonAction>();
  private target: Window | null = null;
  /** The mouse over the game canvas (client px), or null when it is over the HUD / a menu / gone. */
  private pointerAt: { x: number; y: number } | null = null;

  private onKeyDown = (e: KeyboardEvent): void => {
    if (PREVENT_DEFAULT_CODES.has(e.code)) e.preventDefault();
    this.lastDevice = 'keyboard';
    if (e.repeat) return;
    this.keysDown.add(e.code);
    for (const a of BUTTON_ACTIONS) {
      if (this.bindings.keys[a].includes(e.code)) this.queuePress(a);
    }
  };
  private onKeyUp = (e: KeyboardEvent): void => {
    this.keysDown.delete(e.code);
  };
  private onBlur = (): void => {
    this.keysDown.clear();
  };
  private lastWheelAt = -Infinity;
  private onMouseDown = (e: MouseEvent): void => {
    if (!overCanvas(e)) return;
    this.lastDevice = 'keyboard';
    const code = `Mouse${e.button}`;
    if (this.keysDown.has(code)) return;
    this.keysDown.add(code);
    for (const a of BUTTON_ACTIONS) {
      if (this.bindings.keys[a].includes(code)) this.queuePress(a);
    }
  };
  private onMouseUp = (e: MouseEvent): void => {
    // Released anywhere (the cursor may have left the canvas while the button was down).
    this.keysDown.delete(`Mouse${e.button}`);
  };
  private onWheel = (e: WheelEvent): void => {
    if (!overCanvas(e) || e.deltaY === 0) return;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - this.lastWheelAt < WHEEL_REPEAT_MS) return;
    this.lastWheelAt = now;
    const code = e.deltaY > 0 ? 'WheelDown' : 'WheelUp';
    for (const a of BUTTON_ACTIONS) {
      if (this.bindings.keys[a].includes(code)) this.queuePress(a);
    }
  };
  private onMouseMove = (e: MouseEvent): void => {
    if (!overCanvas(e)) {
      this.pointerAt = null;
      return;
    }
    if (this.pointerAt) {
      this.pointerAt.x = e.clientX;
      this.pointerAt.y = e.clientY;
    } else this.pointerAt = { x: e.clientX, y: e.clientY };
  };
  private onContextMenu = (e: MouseEvent): void => {
    // The right button aims; don't let the browser menu pop up over the game.
    if (overCanvas(e)) e.preventDefault();
  };

  attach(target: Window): void {
    this.target = target;
    target.addEventListener('keydown', this.onKeyDown);
    target.addEventListener('keyup', this.onKeyUp);
    target.addEventListener('blur', this.onBlur);
    target.addEventListener('mousedown', this.onMouseDown);
    target.addEventListener('mouseup', this.onMouseUp);
    target.addEventListener('mousemove', this.onMouseMove);
    target.addEventListener('wheel', this.onWheel, { passive: true });
    target.addEventListener('contextmenu', this.onContextMenu);
  }

  detach(): void {
    if (!this.target) return;
    this.target.removeEventListener('keydown', this.onKeyDown);
    this.target.removeEventListener('keyup', this.onKeyUp);
    this.target.removeEventListener('blur', this.onBlur);
    this.target.removeEventListener('mousedown', this.onMouseDown);
    this.target.removeEventListener('mouseup', this.onMouseUp);
    this.target.removeEventListener('mousemove', this.onMouseMove);
    this.target.removeEventListener('wheel', this.onWheel);
    this.target.removeEventListener('contextmenu', this.onContextMenu);
    this.target = null;
  }

  private queuePress(a: ButtonAction): void {
    this.pendingStep.add(a);
    this.pendingFrame.add(a);
  }

  /** Once per rendered frame: poll gamepads, latch frame presses. */
  beginFrame(): void {
    this.pollGamepad();
    this.framePressedSet = this.pendingFrame;
    this.pendingFrame = new Set();
  }

  /** Before every fixed step: latch step presses. */
  beginStep(): void {
    this.stepPressed = this.pendingStep;
    this.pendingStep = new Set();
  }

  private pollGamepad(): void {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    if (!nav || typeof nav.getGamepads !== 'function') return;
    const pads = nav.getGamepads();
    let pad: Gamepad | null = null;
    for (const p of pads) {
      if (p && p.connected) {
        pad = p;
        break;
      }
    }
    if (!pad) {
      this.padButtonsDown.clear();
      this.padButtonValues.length = 0;
      this.padAxesValues.length = 0;
      return;
    }
    const nowDown = new Set<number>();
    pad.buttons.forEach((b, i) => {
      this.padButtonValues[i] = b.value;
      if (b.pressed) nowDown.add(i);
    });
    pad.axes.forEach((v, i) => {
      this.padAxesValues[i] = Math.abs(v) < GAMEPAD_DEADZONE ? 0 : (v - Math.sign(v) * GAMEPAD_DEADZONE) / (1 - GAMEPAD_DEADZONE);
    });
    // The two sticks (standard mapping 0/1 left, 2/3 right) get a radial zone instead (radialDeadzone).
    for (const i of [0, 2]) {
      if (pad.axes.length <= i + 1) continue;
      const [x, y] = radialDeadzone(pad.axes[i]!, pad.axes[i + 1]!, GAMEPAD_DEADZONE);
      this.padAxesValues[i] = x;
      this.padAxesValues[i + 1] = y;
    }
    for (const i of nowDown) {
      if (this.padButtonsDown.has(i)) continue;
      this.lastDevice = 'gamepad';
      for (const a of BUTTON_ACTIONS) if (this.bindings.padButtons[a].includes(i)) this.queuePress(a);
    }
    if (this.padAxesValues.some((v) => v !== 0)) this.lastDevice = 'gamepad';
    this.padButtonsDown = nowDown;
  }

  /** Button currently held. */
  held(a: ButtonAction): boolean {
    if (!this.enabled) return false;
    if (this.virtualButtons.has(a) || this.touchButtons.has(a)) return true;
    for (const k of this.bindings.keys[a]) if (this.keysDown.has(k)) return true;
    for (const b of this.bindings.padButtons[a]) if (this.padButtonsDown.has(b)) return true;
    return false;
  }

  /** Pressed this fixed step (edge). */
  pressed(a: ButtonAction): boolean {
    return this.enabled && this.stepPressed.has(a);
  }

  /** Pressed this render frame (edge). Use for UI / pause. */
  framePressed(a: ButtonAction): boolean {
    return this.framePressedSet.has(a);
  }

  /** Axis value in -1..1, the strongest of keyboard, gamepad and virtual sources. */
  axis(name: AxisName): number {
    return this.readAxis(name).value;
  }

  /**
   * Which device the axis value comes from right now (null when nothing moves it). Driving input
   * shaping (src/input/driveShaping.ts) treats digital keys, a pad stick and the touch stick differently.
   */
  axisSource(name: AxisName): AxisSource {
    return this.readAxis(name).source;
  }

  private readonly axisScratch: { value: number; source: AxisSource } = { value: 0, source: null };

  private readAxis(name: AxisName): { value: number; source: AxisSource } {
    const out = this.axisScratch;
    out.value = 0;
    out.source = null;
    if (!this.enabled) return out;
    const v = this.virtualAxes.get(name);
    if (v !== undefined) {
      out.value = v;
      out.source = 'virtual';
      return out;
    }
    const kb = this.bindings.axes[name];
    let key = 0;
    for (const k of kb.positive) if (this.keysDown.has(k)) key += 1;
    for (const k of kb.negative) if (this.keysDown.has(k)) key -= 1;
    key = Math.max(-1, Math.min(1, key));
    let pad = 0;
    for (const b of this.bindings.padAxes[name]) {
      let val = 0;
      if (b.axis !== undefined) val = (this.padAxesValues[b.axis] ?? 0) * (b.invert ? -1 : 1);
      if (b.positiveButton !== undefined || b.negativeButton !== undefined) {
        val =
          (b.positiveButton !== undefined ? this.padButtonValues[b.positiveButton] ?? 0 : 0) -
          (b.negativeButton !== undefined ? this.padButtonValues[b.negativeButton] ?? 0 : 0);
      }
      if (Math.abs(val) > Math.abs(pad)) pad = val;
    }
    const touch = this.touchAxes.get(name) ?? 0;
    if (Math.abs(pad) > Math.abs(key)) {
      out.value = pad;
      out.source = 'gamepad';
    } else {
      out.value = key;
      out.source = key !== 0 ? 'keyboard' : null;
    }
    if (Math.abs(touch) > Math.abs(out.value)) {
      out.value = touch;
      out.source = 'touch';
    }
    return out;
  }

  /**
   * The pad's right stick (standard mapping axes 2 / 3), x right and y UP, after the radial dead zone:
   * free aim on foot (weapons). A virtual value (`setVirtualAim`, tests) wins.
   */
  aimStick(): { x: number; y: number } {
    if (!this.enabled) return { x: 0, y: 0 };
    if (this.virtualAim) return { ...this.virtualAim };
    return { x: this.padAxesValues[2] ?? 0, y: -(this.padAxesValues[3] ?? 0) };
  }

  /**
   * Free aim with the mouse: where the cursor is over the game canvas (client px) while the RIGHT button
   * is down, else null. Only that button turns the cursor into the aim: K aims too, and a mouse left
   * resting over the canvas must not steal a keyboard player's aim. A virtual cursor (tests) always counts.
   */
  aimCursor(): { x: number; y: number } | null {
    if (!this.enabled) return null;
    if (this.virtualPointer) return { ...this.virtualPointer };
    return this.keysDown.has('Mouse2') && this.pointerAt ? { ...this.pointerAt } : null;
  }

  private virtualAim: { x: number; y: number } | null = null;
  private virtualPointer: { x: number; y: number } | null = null;

  /** Tests: hold the right stick (null releases it). */
  setVirtualAim(v: { x: number; y: number } | null): void {
    this.virtualAim = v ? { x: v.x, y: v.y } : null;
  }

  /** Tests: put the mouse at client px over the canvas (null takes it away). */
  setVirtualPointer(p: { x: number; y: number } | null): void {
    this.virtualPointer = p ? { x: p.x, y: p.y } : null;
  }

  // ------------------------------------------------------------ touch (on-screen controls)

  /** An on-screen button went down / up. Going down counts as a press (edge) like a key. */
  setTouchButton(a: ButtonAction, down: boolean): void {
    if (down) {
      this.lastDevice = 'touch';
      if (!this.touchButtons.has(a)) this.queuePress(a);
      this.touchButtons.add(a);
    } else this.touchButtons.delete(a);
  }

  /** Axis values from the on-screen stick / pedals (-1..1); omitted axes keep their value. */
  setTouchAxes(axes: Partial<Record<AxisName, number>>): void {
    for (const [k, v] of Object.entries(axes) as [AxisName, number][]) {
      const val = Math.max(-1, Math.min(1, Number.isFinite(v) ? v : 0));
      if (val !== 0) this.lastDevice = 'touch';
      if (val === 0) this.touchAxes.delete(k);
      else this.touchAxes.set(k, val);
    }
  }

  /** Release everything the touch layer holds (layer hidden, finger cancelled, tab hidden). */
  clearTouch(): void {
    this.touchAxes.clear();
    this.touchButtons.clear();
  }

  /** Touch state read-out (tests / __bh). */
  touchState(): { axes: Record<string, number>; buttons: ButtonAction[] } {
    return { axes: Object.fromEntries(this.touchAxes), buttons: [...this.touchButtons] };
  }

  // ------------------------------------------------------------ virtual input (tests, __bh)

  /** Hold virtual axes/buttons. Keys may be axis names or button actions. */
  setVirtual(input: Record<string, number | boolean>): void {
    for (const [k, v] of Object.entries(input)) {
      if ((AXES as readonly string[]).includes(k)) {
        this.virtualAxes.set(k as AxisName, typeof v === 'boolean' ? (v ? 1 : 0) : v);
      } else if ((BUTTON_ACTIONS as readonly string[]).includes(k)) {
        const a = k as ButtonAction;
        if (v) {
          if (!this.virtualButtons.has(a)) this.queuePress(a);
          this.virtualButtons.add(a);
        } else this.virtualButtons.delete(a);
      } else {
        throw new Error(`Unknown input '${k}'. Axes: ${AXES.join(',')}; buttons: ${BUTTON_ACTIONS.join(',')}`);
      }
    }
  }

  clearVirtual(): void {
    this.virtualAxes.clear();
    this.virtualButtons.clear();
    this.virtualAim = null;
    this.virtualPointer = null;
  }

  /** Simulate a single press (edge) of a button action. */
  pulse(a: ButtonAction): void {
    this.queuePress(a);
  }
}
